import { getRoot, Instance, types } from 'mobx-state-tree';
import {
  CheckboxGroupModel,
  CheckboxModel,
  ComponentModel,
  ConfirmationModalModel,
  DurationModel,
  ModalModel,
  NewsletterListElementModel,
  NewsletterListElementModelType,
  PageModel,
  PageRoutes,
  RootType,
  TextInputModel,
} from '../../../../internal';
import { validateURL } from '../../../../validators/validators';
import { dateFormatter } from '../../../../utils/date';

export const CreateNewsletterPageModel = types
  .compose(
    PageModel,
    types.model({
      record: types.maybe(NewsletterListElementModel),
      fields: types.maybe(types.array(ComponentModel)),
    })
  )
  .views((self) => {
    return {
      get root(): RootType {
        return getRoot(self);
      },
    };
  })
  .actions((self) => {
    return {
      async beforePageEnter() {
        const id = self.root.router.queryParams.id;

        if (!id) return;
        const record = await self.root.api.newsletters.get(id);
        if (!record.data) return;
        this.setRecord(record.data);
      },
      setRecord(record: NewsletterListElementModelType) {
        self.record = record;
      },
      navigateToBannersPage(type: string) {
        self.root.router.navigate({
          newView: PageRoutes.Banners.id,
        });
      },
      getConfirmationModal() {
        return (
          self.components.get('confirmation_modal') ||
          self.addComponent(
            ConfirmationModalModel.create({
              id: 'confirmation_modal',
              labelNo: 'basic:no',
              labelYes: 'basic:yes',
              afterYes: this.deleteNewsletter,
              modal: ModalModel.create({
                id: 'modal',
                opened: false,
                showCloseButton: false,
              }),
            })
          )
        );
      },
      openConfirmationModal() {
        this.getConfirmationModal().open();
      },
      async deleteNewsletter() {
        const id = self.root.router.queryParams.id;
        const type = self.root.router.queryParams.type;
        await self.root.api.newsletters.delete(id);
        this.navigateToBannersPage(type);
      },
      async submitData() {
        const components = self.components;
        const data = {
          name: components.get('name').value,
          status: Number(
            components.get('checkbox_group').checkboxes.get('is_active')
              ?.isChecked
              ? 1
              : 0
          ),
          url: components.get('url').value,
          dateStart: dateFormatter(
            components.get('newsletter_duration').fromDate
          ),
          dateEnd: dateFormatter(components.get('newsletter_duration').toDate),
          showEveryTime: Number(
            components.get('checkbox_group').checkboxes.get('show_every_time')
              ?.isChecked
              ? 1
              : 0
          ),
        };
        const id = self.root.router.queryParams.id;
        if (id) {
          await self.root.api.newsletters.update(id, data);
        } else {
          await self.root.api.newsletters.create(data);
        }
        const type = self.root.router.queryParams.type;
        this.navigateToBannersPage(type);
      },
      getUrl() {
        return (
          self.components.get('url') ||
          self.addComponent(
            TextInputModel.create({
              id: 'url',
              label: 'banners:url',
              isRequired: true,
              validators: {
                validateURL: validateURL,
              },
              value: self.record?.url || '',
            })
          )
        );
      },
      getName() {
        return (
          self.components.get('name') ||
          self.addComponent(
            TextInputModel.create({
              id: 'name',
              label: 'banners:name',
              isRequired: true,
              value: self.record?.name || '',
            })
          )
        );
      },
      getNewsletterDuration() {
        return (
          self.components.get('newsletter_duration') ||
          self.addComponent(
            DurationModel.create({
              id: 'newsletter_duration',
              fromDate: self.record?.dateStart
                ? new Date(self.record?.dateStart)
                : new Date(),
              toDate: self.record?.dateEnd
                ? new Date(self.record?.dateEnd)
                : new Date(),
              maxDuration: 31,
              disabledDates: [],
              minDate: new Date(),
              modal: ModalModel.create({
                id: 'NewsletterDurationModal',
                label: 'NewsletterDuration Duration',
                opened: false,
                showCloseButton: true,
              }),
            })
          )
        );
      },
      getCheckboxGroup() {
        return (
          self.components.get('checkbox_group') ||
          self.addComponent(
            CheckboxGroupModel.create({
              id: 'checkbox_group',
              selectedCheckboxes: {
                ...(self.record?.status === 1
                  ? { is_active: 'is_active' }
                  : {}),
                ...(self.record?.showEveryTime
                  ? { show_every_time: 'show_every_time' }
                  : {}),
              },
              checkboxes: {
                is_active: CheckboxModel.create({
                  id: 'is_active',
                  label: 'banners:is_active',
                }),
                show_every_time: CheckboxModel.create({
                  id: 'show_every_time',
                  label: 'banners:show_every_time',
                }),
              },
            })
          )
        );
      },
    };
  })
  .named('CreateNewsletterModel');

export type CreateNewsletterModelType = Instance<
  typeof CreateNewsletterPageModel
>;
